import { useEffect, useState } from "react";
import axios from "axios";
import { AlertTriangle, ExternalLink } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";

let showDialog = null;
const CODES = ["duplicate", "duplicate_check_failed"];

const ask = (detail) => new Promise((resolve) => (showDialog ? showDialog(detail, resolve) : resolve(null)));

export const installDuplicateGuard = () => {
  axios.interceptors.response.use(null, async (error) => {
    const res = error?.response;
    const detail = res?.data?.detail;
    if (res?.status !== 409 || !CODES.includes(detail?.code)) throw error;
    if (error.config?._dupRetried) {
      res.data.detail = String(detail.message || "Possible duplicate - not created");
      throw error;
    }
    const reason = await ask(detail);
    if (!reason) {
      res.data.detail = detail.message || "Possible duplicate - not created";
      throw error;
    }
    const cfg = { ...error.config, _dupRetried: true, params: { ...(error.config.params || {}), force_duplicate: true, duplicate_reason: reason } };
    return axios.request(cfg);
  });
};

const DupRow = ({ d }) => (
  <div className="border border-red-200 bg-red-50 rounded-lg p-3 flex items-start justify-between gap-3" data-testid={`dup-guard-row-${d.pf_number}`}>
    <div className="text-sm">
      <div className="font-semibold text-red-800">{d.pf_number} <span className="font-normal text-gray-600">{d.customer || ""}</span></div>
      <div className="text-xs text-red-700">{(d.matched_on || []).join(" · ")}</div>
      <div className="text-xs text-gray-500">{(d.created_at || "").slice(0, 10)} {d.created_by ? `· ${d.created_by}` : ""}</div>
    </div>
    <Button size="sm" variant="outline" data-testid={`dup-guard-open-${d.pf_number}`}
      onClick={() => window.open(`/position-folders?search=${encodeURIComponent(d.pf_number)}`, "_blank")}>
      <ExternalLink className="h-3.5 w-3.5 mr-1" />Open existing PF
    </Button>
  </div>
);

export const DuplicateGuardDialog = () => {
  const [state, setState] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    showDialog = (detail, resolve) => { setReason(""); setState({ detail, resolve }); };
    return () => { showDialog = null; };
  }, []);

  const close = (value) => { state?.resolve(value); setState(null); };
  const failed = state?.detail?.code === "duplicate_check_failed";
  const dups = state?.detail?.duplicates || [];

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && close(null)}>
      <DialogContent className="max-w-2xl" data-testid="dup-guard-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <AlertTriangle className="h-5 w-5" />{failed ? "Duplicate check failed" : "Possible duplicate shipment"}
          </DialogTitle>
          <DialogDescription data-testid="dup-guard-message">
            {failed ? state?.detail?.message : "This container / MBL / HBL / booking already exists in a PF from the last 3 months. Open the existing PF instead, or create anyway with a reason."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 max-h-72 overflow-y-auto">{dups.map((d) => <DupRow key={d.id || d.pf_number} d={d} />)}</div>
        <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason for creating anyway (required, logged)"
          data-testid="dup-guard-reason-input" />
        <DialogFooter>
          <Button variant="outline" onClick={() => close(null)} data-testid="dup-guard-cancel-btn">Cancel</Button>
          <Button variant="destructive" disabled={!reason.trim()} onClick={() => close(reason.trim())} data-testid="dup-guard-create-anyway-btn">
            Create anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
